import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "../../button";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { Input } from "../../input";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import TextFilter from "../../customs/text-filter";
import { SelectV2 } from "../../select-v2";
import type {
  DataTableNumberRangeValue,
  DataTableDateRangeValue,
  DataTableOption,
  DataTableStringFilter,
  DataTableNumberRangeFilter,
  DataTableDateRangeFilter,
  DataTableDropdownFilter,
} from "./types";

const getNumberRangeHasValue = (value: DataTableNumberRangeValue) =>
  value.min !== null || value.max !== null;

const getDateRangeHasValue = (value: DataTableDateRangeValue) =>
  value.from.trim() !== "" || value.to.trim() !== "";

const resolveOpenState = (
  controlledOpen: boolean | undefined,
  internalOpen: boolean,
) => (controlledOpen !== undefined ? controlledOpen : internalOpen);

type PopoverControlProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
};

const FilterTrigger = ({ active }: { active: boolean }) => (
  <img
    className={`ml-2 w-3 cursor-pointer ${active ? "opacity-100" : "opacity-80"}`}
    src={MagnifyingGlass}
    alt="Search"
  />
);

export const DataTableStringFilterPopover = ({
  filter,
  isOpen,
  onOpenChange,
  showTrigger = true,
}: {
  filter: DataTableStringFilter;
} & PopoverControlProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [tempValue, setTempValue] = useState(filter.value || "");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const controlledOpen = resolveOpenState(isOpen, internalOpen);
  const setOpen = onOpenChange || setInternalOpen;
  const hasValue = filter.value.trim() !== "";

  useEffect(() => {
    setTempValue(filter.value || "");
  }, [filter.value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const commitValue = (value: string) => {
    filter.onFilter(value);
  };

  const handleChange = (value: string) => {
    setTempValue(value);

    if (filter.submitMode === "enter") {
      return;
    }

    if (filter.debounceMs && filter.debounceMs > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        commitValue(value);
      }, filter.debounceMs);
      return;
    }

    commitValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filter.submitMode === "enter" && e.key === "Enter") {
      commitValue(tempValue.trim());
    }
  };

  return (
    <Popover open={controlledOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {showTrigger ? <FilterTrigger active={hasValue} /> : <button type="button" className="sr-only" aria-hidden="true" />}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <TextFilter
          value={tempValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={filter.placeholder || "Search..."}
          title={typeof filter.title === "string" ? filter.title : null}
          description={typeof filter.description === "string" ? filter.description : null}
        />
      </PopoverContent>
    </Popover>
  );
};

export const DataTableNumberRangeFilterPopover = ({
  filter,
  isOpen,
  onOpenChange,
  showTrigger = true,
}: {
  filter: DataTableNumberRangeFilter;
} & PopoverControlProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [localMin, setLocalMin] = useState(
    filter.value.min !== null ? filter.value.min.toString() : "",
  );
  const [localMax, setLocalMax] = useState(
    filter.value.max !== null ? filter.value.max.toString() : "",
  );
  const controlledOpen = resolveOpenState(isOpen, internalOpen);
  const setOpen = onOpenChange || setInternalOpen;
  const hasValue = getNumberRangeHasValue(filter.value);

  useEffect(() => {
    setLocalMin(filter.value.min !== null ? filter.value.min.toString() : "");
    setLocalMax(filter.value.max !== null ? filter.value.max.toString() : "");
  }, [filter.value.min, filter.value.max]);

  const handleMinChange = (value: string) => {
    setLocalMin(value);
    filter.onFilter({
      min: value === "" ? null : Number.isNaN(Number(value)) ? null : parseInt(value, 10),
      max: filter.value.max,
    });
  };

  const handleMaxChange = (value: string) => {
    setLocalMax(value);
    filter.onFilter({
      min: filter.value.min,
      max: value === "" ? null : Number.isNaN(Number(value)) ? null : parseInt(value, 10),
    });
  };

  const handleClear = () => {
    setLocalMin("");
    setLocalMax("");
    filter.onFilter({ min: null, max: null });
  };

  return (
    <Popover open={controlledOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {showTrigger ? <FilterTrigger active={hasValue} /> : <button type="button" className="sr-only" aria-hidden="true" />}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{filter.title}</h4>
            {(localMin !== "" || localMax !== "") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {filter.minLabel || "Min"}
              </label>
              <Input
                type="number"
                value={localMin}
                onChange={(e) => handleMinChange(e.target.value)}
                placeholder={filter.placeholder || "Enter number"}
                className="w-full"
                min="0"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {filter.maxLabel || "Max"}
              </label>
              <Input
                type="number"
                value={localMax}
                onChange={(e) => handleMaxChange(e.target.value)}
                placeholder={filter.placeholder || "Enter number"}
                className="w-full"
                min="0"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const DataTableDateRangeFilterPopover = ({
  filter,
  isOpen,
  onOpenChange,
  showTrigger = true,
}: {
  filter: DataTableDateRangeFilter;
} & PopoverControlProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(filter.value.from || "");
  const [localTo, setLocalTo] = useState(filter.value.to || "");
  const controlledOpen = resolveOpenState(isOpen, internalOpen);
  const setOpen = onOpenChange || setInternalOpen;
  const hasValue = getDateRangeHasValue(filter.value);

  useEffect(() => {
    setLocalFrom(filter.value.from || "");
    setLocalTo(filter.value.to || "");
  }, [filter.value.from, filter.value.to]);

  const handleClear = () => {
    setLocalFrom("");
    setLocalTo("");
    filter.onFilter({ from: "", to: "" });
  };

  return (
    <Popover open={controlledOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {showTrigger ? <FilterTrigger active={hasValue} /> : <button type="button" className="sr-only" aria-hidden="true" />}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{filter.title}</h4>
            {(localFrom !== "" || localTo !== "") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {filter.fromLabel || "From Date"}
              </label>
              <Input
                type="date"
                value={localFrom}
                onChange={(e) => {
                  const from = e.target.value;
                  setLocalFrom(from);
                  filter.onFilter({ from, to: localTo });
                }}
                className="w-full"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {filter.toLabel || "To Date"}
              </label>
              <Input
                type="date"
                value={localTo}
                onChange={(e) => {
                  const to = e.target.value;
                  setLocalTo(to);
                  filter.onFilter({ from: localFrom, to });
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const DataTableDropdownFilterPopover = ({
  filter,
  isOpen,
  onOpenChange,
  showTrigger = true,
}: {
  filter: DataTableDropdownFilter;
} & PopoverControlProps & {
  showTrigger?: boolean;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlledOpen = resolveOpenState(isOpen, internalOpen);
  const setOpen = onOpenChange || setInternalOpen;
  const hasValue = filter.value.trim() !== "";

  const handleClear = () => {
    filter.onFilter("");
    setOpen(false);
  };

  return (
    <Popover open={controlledOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {showTrigger ? (
          <FilterTrigger active={hasValue} />
        ) : (
          <button type="button" className="sr-only" aria-hidden="true" />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <SelectV2
            value={filter.value}
            onChange={(value) => {
              filter.onFilter(value);
            }}
            options={filter.options}
            label={filter.title || "Filter"}
            placeholder={filter.placeholder || "Select..."}
            className="w-full"
            triggerClassName="w-full"
            clearable={false}
          />

          {hasValue && (
            <div className="border-t border-gray-200 pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-8 w-full justify-start px-3 text-left text-sm text-gray-500 hover:text-gray-700"
              >
                <X className="mr-2 h-4 w-4" />
                {filter.clearLabel || "Clear"}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
